import { Trans, useTranslation } from 'react-i18next';
import './UpgradeToPremium.scss';
import Subtitle from 'components/TextComps/Subtitle';
import { ReactComponent as Crown } from 'images/icons/Crown.svg';
import { ReactComponent as Checkmark } from 'images/icons/Checkmark.svg';
import { Button } from '@trackman/web-shared-components';
import { useCartStore } from 'stores/Webshop/CartStore';
import { CartItem } from 'types/cart';
import { AddedToCart } from '../../AddedToCart/AddedToCart';
import { getSubscription, renewablePlan } from 'constants/subscription';
import { addCartItemToLocalStorage } from 'utilities/cartLocalStorageHelper';
import { useStores } from 'index';
import { useGetSubscriptionPrice } from 'queryHooks/subscriptionPrice';
import { Skeleton } from '@mui/material';

export const UpgradeToPremium = () => {
  const { t } = useTranslation();
  const notificationsStore = useStores().notificationStore;

  const itemsInCart: CartItem[] = useCartStore((state) => state.items);
  const addItemToCart = useCartStore((state) => state.addItemToCart);
  const { data: subscriptionPrice, isPending: isSubscriptionPricePending } = useGetSubscriptionPrice(renewablePlan);

  const handleAddToCart = () => {
    addItemToCart(getSubscription(subscriptionPrice || 0));
    addCartItemToLocalStorage(getSubscription(subscriptionPrice || 0));
    notificationsStore.addToast(
      t('AddedToCart', { ns: 'webshop' }),
      t('CertificateHasBeenAddedToCart', { ns: 'webshop', certificateName: getSubscription(subscriptionPrice || 0).title }),
      'success'
    );
  };

  return (
    <div className='upgrade-to-premium'>
      <Subtitle>
        <>{t('UpgradeToPremium', { ns: 'webshop' })}</>
      </Subtitle>

      <div className='plans-container'>
        {isSubscriptionPricePending ? (
          <>
            <PlanLoaderCard />
            <PlanLoaderCard />
          </>
        ) : (
          <>
            <FreePlanCard />
            <PremiumPlanCard
              subscriptionPrice={subscriptionPrice || 0}
              isItemInCart={itemsInCart.some((item) => item.id === 'premium')}
              addItemToCart={handleAddToCart}
            />
          </>
        )}
      </div>
    </div>
  );
};

const BenefitBulletPoint = ({ isPremiumPlan }: { isPremiumPlan: boolean }) => {
  return (
    <div
      className={isPremiumPlan ? 'benefit-bullet-point benefit-bullet-point__premium' : 'benefit-bullet-point benefit-bullet-point__free'}
    >
      <Checkmark />
    </div>
  );
};

const FreePlanCard = () => {
  const { t } = useTranslation();

  return (
    <div className='plan-card'>
      <div className='free-plan-badge'>{t('CurrentPlan', { ns: 'webshop' })}</div>
      <Subtitle>{t('Basic', { ns: 'webshop' })}</Subtitle>

      <h2>{t('Free', { ns: 'webshop' })}</h2>
      <p>{t('GainAccessToResources', { ns: 'webshop' })}</p>
      <div className='benefits-list'>
        <div className='benefit-item'>
          <BenefitBulletPoint isPremiumPlan={false} />
          {t('LearningMaterialsAndParameters', { ns: 'webshop' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint isPremiumPlan={false} />
          {t('PayPerEvent', { ns: 'webshop' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint isPremiumPlan={false} />
          {t('PayPerCertificate', { ns: 'webshop' })}
        </div>
      </div>
    </div>
  );
};

const PremiumPlanCard = ({
  subscriptionPrice,
  isItemInCart,
  addItemToCart,
}: {
  subscriptionPrice: number;
  isItemInCart: boolean;
  addItemToCart: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className='plan-card premium-card'>
      <div className='premium-card-content-wrapper'>
        <Subtitle>
          <>
            <div className='premium-plan-header'>
              <Crown className='premium-plan-icon' />
              {t('Premium', { ns: 'webshop' })}
            </div>
          </>
        </Subtitle>

        <h2>
          <Trans
            i18nKey='PriceWithYear'
            ns='webshop'
            defaults={`<0>{{price}}</0><1>/year</1>`}
            values={{ price: subscriptionPrice || 0 }}
            components={[<span key='0' />, <span key='1' className='' />]}
          />
        </h2>

        <p>{t('GainAccessToMoreToolsAndGoodies', { ns: 'webshop' })}</p>
        <div className='benefits-list'>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('-50%OffCertificates', { ns: 'webshop' })}
          </div>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('LearningMaterialsAndParameters', { ns: 'webshop' })}
          </div>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('EventsFreeOfCharge', { ns: 'webshop' })}
          </div>
          <div className='benefit-item'>
            <BenefitBulletPoint isPremiumPlan={true} />
            {t('AccessToOnlineLearningPlatform', { ns: 'webshop' })}
          </div>
        </div>
      </div>

      {!isItemInCart ? (
        <Button className='button' variant='contained' onClick={() => addItemToCart()}>
          {t('Subscribe', { ns: 'webshop' })}
        </Button>
      ) : (
        <AddedToCart />
      )}
    </div>
  );
};

const PlanLoaderCard = () => {
  return (
    <div className='plan-card'>
      <Skeleton width={80} height={20} />
      <Skeleton width={100} height={30} sx={{ marginTop: '16px', marginBottom: '8px' }} />
      <Skeleton width={250} height={20} sx={{ marginBottom: '24px' }} />
      <Skeleton width={350} height={20} />
      <Skeleton width={200} height={20} />
      <Skeleton width={250} height={20} />
    </div>
  );
};
